/**
 * Dimensioning Tools
 * 
 * Provides 2D-style dimensioning:
 * - Linear Dimension (horizontal/vertical)
 * - Aligned Dimension (along object)
 * - Radius Dimension
 * - Diameter Dimension
 * - Angular Dimension
 * - Dimension Styles
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useAppStore } from '../../state/store';
import { GROUND_PLANE } from '../../core/scene';
import { constrainDraftPoint } from '../snapping';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../../utils/settingsPersistence';
import { useAnnotationInk } from '../../hooks/useThemeInk';

/**
 * Resolve a dimension style's stored line/text color to paint color.
 * Styles that predate theming default to green lines ('#00ff00') and the
 * persisted store carries that forward — treat the legacy default as
 * "unset" so dimensions follow the theme ink (white on dark, black on
 * light). Explicitly customized colors keep rendering as configured.
 */
export const dimStyleColor = (styleColor: string | undefined, ink: string): string =>
    !styleColor || styleColor === '#00ff00' ? ink : styleColor;

// ============================================================================
// DIMENSION STYLES
// ============================================================================

export interface DimensionStyle {
    id: string;
    name: string;
    // Text
    textHeight: number;
    textColor: string;
    textFont: string;
    textGap: number;      // Gap between text and dimension line
    // Lines
    lineColor: string;
    lineWeight: number;
    extensionOffset: number;    // Start offset from point
    extensionOvershoot: number; // Extension beyond dimension line
    // Arrows
    arrowType: 'open' | 'closed' | 'dot' | 'tick' | 'none';
    arrowSize: number;
    // Units
    precision: number;    // Decimal places
    suffix: string;       // e.g., " mm" or "'"
    scale: number;        // Dimension scale factor
}

interface DimensionStyleState {
    styles: Record<string, DimensionStyle>;
    activeStyleId: string;
    addStyle: (style: DimensionStyle) => void;
    updateStyle: (id: string, updates: Partial<DimensionStyle>) => void;
    deleteStyle: (id: string) => void;
    setActiveStyle: (id: string) => void;
    getActiveStyle: () => DimensionStyle;
    getStyleFor: (styleId?: string) => DimensionStyle;
}

const defaultDimStyle: DimensionStyle = {
    id: 'standard',
    name: 'Standard',
    textHeight: 0.15,
    textColor: '#00ff00',
    textFont: 'Arial',
    textGap: 0.1,
    lineColor: '#00ff00',
    lineWeight: 1,
    extensionOffset: 0.1,
    extensionOvershoot: 0.15,
    arrowType: 'closed',
    arrowSize: 0.15,
    precision: 2,
    suffix: '',
    scale: 1,
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDimStyleStore = create<DimensionStyleState>()(persist((set, get) => ({
    styles: {
        standard: defaultDimStyle,
        architectural: {
            ...defaultDimStyle,
            id: 'architectural',
            name: 'Architectural',
            arrowType: 'tick',
            precision: 0,
            suffix: ' cm',
            scale: 100,
        },
        metric: {
            ...defaultDimStyle,
            id: 'metric',
            name: 'Metric',
            suffix: ' m',
            precision: 3,
        },
    },
    activeStyleId: 'standard',

    addStyle: (style) => set((state) => ({
        styles: { ...state.styles, [style.id]: style }
    })),

    updateStyle: (id: string, updates: Partial<DimensionStyle>) => set((state) => (
        state.styles[id]
            ? { styles: { ...state.styles, [id]: { ...state.styles[id], ...updates } } }
            : state
    )),

    deleteStyle: (id: string) => set((state) => {
        if (!state.styles[id] || id === state.activeStyleId) return state;
        const next = { ...state.styles };
        delete next[id];
        return { styles: next };
    }),

    setActiveStyle: (id) => set({ activeStyleId: id }),

    getActiveStyle: () => {
        const state = get();
        return state.styles[state.activeStyleId] || defaultDimStyle;
    },

    /** Resolve a dimension's own styleId first (per-entity style respect),
     *  falling back to the active style when unset/unknown. */
    getStyleFor: (styleId?: string) => {
        const state = get();
        if (styleId && state.styles[styleId]) return state.styles[styleId];
        return state.styles[state.activeStyleId] || defaultDimStyle;
    },
}), {
    name: 'yap-dim-styles',
    storage: settingsStorage,
    // Persist user styles + the active pick; the getter closures are
    // re-created by the factory on load, never serialized.
    partialize: (state) => ({ styles: state.styles, activeStyleId: state.activeStyleId }),
}));

// ============================================================================
// DIMENSION DATA TYPES
// ============================================================================

export interface LinearDimensionData {
    type: 'linear' | 'aligned';
    point1: number[];
    point2: number[];
    textPosition: number[];
    styleId: string;
    override?: string;  // Manual text override
    /** DIMBREAK crossing gap flag (drawn as a gap at the line midpoint). */
    breakAtCrossing?: boolean;
    /** DIMINSPECT quality-control label shown above the dimension text. */
    inspection?: string;
    /** DIMSPACE stagger slot index (drives the perpendicular offset). */
    spaceIndex?: number;
    /** DIMSPACE spacing between staggered parallel dimension lines. */
    spaceOffset?: number;
}

export interface RadialDimensionData {
    type: 'radius' | 'diameter';
    center: number[];
    radiusPoint: number[];
    radius: number;
    styleId: string;
    override?: string;
}

export interface AngularDimensionData {
    type: 'angular';
    center: number[];
    point1: number[];
    point2: number[];
    angle: number;
    styleId: string;
    override?: string;
}

export type DimensionData = LinearDimensionData | RadialDimensionData | AngularDimensionData;

// ============================================================================
// LINEAR DIMENSION TOOL
// ============================================================================

/**
 * Linear/Aligned Dimension Tool
 * 
 * Click 1: First point
 * Click 2: Second point
 * Click 3: Dimension line position
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useLinearDimensionTool(isAligned: boolean = false) {
    const [point1, setPoint1] = useState<THREE.Vector3 | null>(null);
    const [point2, setPoint2] = useState<THREE.Vector3 | null>(null);
    const [previewPos, setPreviewPos] = useState<THREE.Vector3 | null>(null);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);
    const activeStyleId = useDimStyleStore((s) => s.activeStyleId);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === (isAligned ? 'dimAligned' : 'dimLinear');

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    /**
     * Reference points resolve through the shared drafting pipeline (osnap
     * on the raw cursor → F9 grid step; no base, so no polar/ortho), the
     * same contract as the line/polyline hooks — endpoints land exactly on
     * entity vertices/centers and the marker + statusbar readout publish
     * from the same query. No hardcoded grid step: with F9 off the point
     * follows the cursor freely instead of always jumping to a lattice.
     */
    const snapPoint = useCallback((): THREE.Vector3 | null => {
        const point = getGroundPoint();
        return point ? constrainDraftPoint(point, camera, gl, null) : null;
    }, [getGroundPoint, camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        // First two clicks measure real points; the placement click stays
        // unsnapped so the dim line can drop freely (2D behavior).
        const point = (point1 === null || point2 === null) ? snapPoint() : getGroundPoint();
        if (!point) return;

        if (!point1) {
            setPoint1(point.clone());
        } else if (!point2) {
            setPoint2(point.clone());
        } else {
            // Create dimension (undoable: freshly placed dims answer Ctrl+Z)
            undoableAdd([makeEntity('dimension', `Dim-${Date.now()}`, {
                type: isAligned ? 'aligned' : 'linear',
                point1: [point1.x, point1.y, point1.z],
                point2: [point2.x, point2.y, point2.z],
                textPosition: [point.x, point.y, point.z],
                styleId: activeStyleId,
            } as LinearDimensionData as unknown as Record<string, unknown>, activeLayerId)]);

            setPoint1(null);
            setPoint2(null);
            setPreviewPos(null);
        }
    }, [isActive, point1, point2, snapPoint, getGroundPoint, activeLayerId, activeStyleId, isAligned]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        // Preview the same point the click would commit: OSNAP while
        // collecting references, free projection for the drop click.
        const point = (point1 === null || point2 === null) ? snapPoint() : getGroundPoint();
        if (point) {
            setPreviewPos((prev) => (prev && prev.distanceToSquared(point) < 1e-8 ? prev : point));
        }
    }, [isActive, point1, point2, snapPoint, getGroundPoint]);

    // Cancel on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isActive) {
                setPoint1(null);
                setPoint2(null);
                setPreviewPos(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        point1,
        point2,
        previewPos,
        handlePointerDown,
        handlePointerMove,
    };
}

// ============================================================================
// RADIUS DIMENSION TOOL
// ============================================================================

/**
 * Radius Dimension Tool
 * 
 * Click on circle/arc edge to add radius dimension
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRadiusDimensionTool() {
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [radiusPoint, setRadiusPoint] = useState<THREE.Vector3 | null>(null);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);
    const activeStyleId = useDimStyleStore((s) => s.activeStyleId);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'dimRadius' || activeTool === 'dimDiameter';
    const isDiameter = activeTool === 'dimDiameter';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    /** OSNAP-backed pick: center snaps find circle/arc centers, rim snaps
     *  find quadrant/endpoint markers on the same curve. Same shared
     *  drafting pipeline as the linear tools (no hardcoded grid step). */
    const snapPoint = useCallback((): THREE.Vector3 | null => {
        const point = getGroundPoint();
        return point ? constrainDraftPoint(point, camera, gl, null) : null;
    }, [getGroundPoint, camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = snapPoint();
        if (!point) return;

        if (!center) {
            setCenter(point.clone());
        } else {
            const radius = center.distanceTo(point);

            undoableAdd([makeEntity('dimension', `${isDiameter ? 'Dia' : 'Rad'}-${Date.now()}`, {
                type: isDiameter ? 'diameter' : 'radius',
                center: [center.x, center.y, center.z],
                radiusPoint: [point.x, point.y, point.z],
                radius,
                styleId: activeStyleId,
            } as RadialDimensionData as unknown as Record<string, unknown>, activeLayerId)]);

            setCenter(null);
            setRadiusPoint(null);
        }
    }, [isActive, center, isDiameter, snapPoint, activeLayerId, activeStyleId]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !center) return;

        const point = snapPoint();
        if (point) {
            setRadiusPoint((prev) => (prev && prev.distanceToSquared(point) < 1e-8 ? prev : point));
        }
    }, [isActive, center, snapPoint]);

    // Cancel on Escape (same contract as the linear dimension hook).
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isActive) {
                setCenter(null);
                setRadiusPoint(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        center,
        radiusPoint,
        isDiameter,
        handlePointerDown,
        handlePointerMove,
    };
}

// ============================================================================
// DIMENSION RENDERER
// ============================================================================

/**
 * Staged linear/aligned dimension preview: the picked reference points, the
 * rubber band to the cursor and the live distance readout. Rendered by the
 * ToolScope while the dimension hook collects its two reference points.
 */
export const LinearDimensionPreview: React.FC<{
    point1: THREE.Vector3 | null;
    point2: THREE.Vector3 | null;
    cursor: THREE.Vector3 | null;
}> = ({ point1, point2, cursor }) => {
    if (!point1) return null;
    const anchor = point2 ?? point1;
    const dist = anchor.distanceTo(cursor ?? anchor);
    return (
        <group>
            <mesh position={point1}>
                <sphereGeometry args={[0.05, 8, 8]} />
                <meshBasicMaterial color="#00aaff" />
            </mesh>
            {point2 && (
                <mesh position={point2}>
                    <sphereGeometry args={[0.05, 8, 8]} />
                    <meshBasicMaterial color="#00aaff" />
                </mesh>
            )}
            {cursor && (
                <Line points={[anchor, cursor]} color="#00aaff" lineWidth={1.5} dashed />
            )}
            {cursor && point2 && (
                <Text
                    position={anchor.clone().lerp(cursor, 0.5).add(new THREE.Vector3(0, 0, 0.15))}
                    fontSize={0.22}
                    color="#00aaff"
                    anchorX="center"
                    anchorY="bottom"
                >
                    {dist.toFixed(2)}
                </Text>
            )}
        </group>
    );
};

/**
 * Staged radius/diameter dimension preview: center marker plus the leader
 * line from the center to the cursor while the radius point is pending.
 */
export const RadialDimensionPreview: React.FC<{
    center: THREE.Vector3 | null;
    radiusPoint: THREE.Vector3 | null;
    isDiameter: boolean;
}> = ({ center, radiusPoint, isDiameter }) => {
    if (!center) return null;
    const dist = radiusPoint ? center.distanceTo(radiusPoint) : 0;
    return (
        <group>
            <mesh position={center}>
                <sphereGeometry args={[0.05, 8, 8]} />
                <meshBasicMaterial color="#00aaff" />
            </mesh>
            {radiusPoint && (
                <>
                    <Line points={[center, radiusPoint]} color="#00aaff" lineWidth={1.5} dashed />
                    <Text
                        position={radiusPoint.clone().add(new THREE.Vector3(0, 0, 0.15))}
                        fontSize={0.22}
                        color="#00aaff"
                        anchorX="center"
                        anchorY="bottom"
                    >
                        {`${isDiameter ? 'Ø' : 'R'}${(isDiameter ? dist * 2 : dist).toFixed(2)}`}
                    </Text>
                </>
            )}
        </group>
    );
};


/**
 * Linear Dimension Renderer
 */
export const LinearDimensionRenderer: React.FC<{
    data: LinearDimensionData;
    isSelected: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, isSelected, onClick }) => {
    const style = useDimStyleStore((s) => s.getStyleFor(data.styleId));
    const ink = useAnnotationInk();
    const color = isSelected ? '#00aaff' : dimStyleColor(style.lineColor, ink);

    const { p1, p2, dimLine, ext1, ext2, distance, textPos, angle, gap } = useMemo(() => {
        const p1 = new THREE.Vector3(...data.point1);
        const p2 = new THREE.Vector3(...data.point2);
        const textP = new THREE.Vector3(...data.textPosition);

        let dimP1: THREE.Vector3, dimP2: THREE.Vector3;

        if (data.type === 'aligned') {
            // Aligned: dimension line parallel to the measured run, through
            // the drop point. The offset is the PERPENDICULAR component of
            // p1→textP (not its full length), otherwise the line lands at
            // the wrong distance whenever the drop point is not exactly
            // square to the run.
            const dir = new THREE.Vector3().subVectors(p2, p1).normalize();
            // Perpendicular in the XY ground plane (Z-up convention)
            const perp = new THREE.Vector3(-dir.y, dir.x, 0);
            const offset = textP.clone().sub(p1);
            const perpDist = Math.abs(offset.dot(perp));
            const sign = offset.dot(perp) >= 0 ? 1 : -1;

            dimP1 = p1.clone().add(perp.clone().multiplyScalar(sign * perpDist));
            dimP2 = p2.clone().add(perp.clone().multiplyScalar(sign * perpDist));
        } else {
            // Linear: horizontal or vertical dimension line (XY plane, Z-up)
            const isHorizontal = Math.abs(textP.y - p1.y) > Math.abs(textP.x - p1.x);

            if (isHorizontal) {
                dimP1 = new THREE.Vector3(p1.x, textP.y, p1.z);
                dimP2 = new THREE.Vector3(p2.x, textP.y, p2.z);
            } else {
                dimP1 = new THREE.Vector3(textP.x, p1.y, p1.z);
                dimP2 = new THREE.Vector3(textP.x, p2.y, p2.z);
            }
        }

        // DIMSPACE: stagger parallel dimensions along the perpendicular.
        if (data.spaceOffset && data.spaceIndex) {
            const dir = new THREE.Vector3().subVectors(dimP2, dimP1).normalize();
            const perp = new THREE.Vector3(-dir.y, dir.x, 0);
            const shift = perp.multiplyScalar(data.spaceOffset * data.spaceIndex);
            dimP1 = dimP1.add(shift);
            dimP2 = dimP2.add(shift);
        }

        // Extension lines
        const ext1Start = p1.clone();
        const ext1End = dimP1.clone().add(
            new THREE.Vector3().subVectors(dimP1, p1).normalize().multiplyScalar(style.extensionOvershoot)
        );
        const ext2Start = p2.clone();
        const ext2End = dimP2.clone().add(
            new THREE.Vector3().subVectors(dimP2, p2).normalize().multiplyScalar(style.extensionOvershoot)
        );

        const distance = dimP1.distanceTo(dimP2);
        const textPos = new THREE.Vector3().addVectors(dimP1, dimP2).multiplyScalar(0.5);
        textPos.z += style.textGap; // lift above the ground plane (Z-up)

        const angle = Math.atan2(dimP2.y - dimP1.y, dimP2.x - dimP1.x);

        // DIMBREAK: split the dimension line into two halves with a gap at
        // the midpoint (crossing-break symbol); null when the flag is off.
        let gap: [THREE.Vector3, THREE.Vector3][] | null = null;
        if (data.breakAtCrossing) {
            const half = distance / 2;
            const g = Math.min(style.arrowSize * 1.5, half * 0.4);
            const dir = new THREE.Vector3().subVectors(dimP2, dimP1).normalize();
            const mid = textPos.clone().setZ(dimP1.z);
            gap = [
                [dimP1, mid.clone().addScaledVector(dir, -g / 2)],
                [mid.clone().addScaledVector(dir, g / 2), dimP2],
            ];
        }

        return {
            p1: dimP1,
            p2: dimP2,
            dimLine: [dimP1, dimP2],
            ext1: [ext1Start, ext1End],
            ext2: [ext2Start, ext2End],
            distance,
            textPos,
            angle,
            gap,
        };
    }, [data, style]);

    const displayText = data.override ||
        (distance * style.scale).toFixed(style.precision) + style.suffix;

    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            {/* Dimension line (split with a break gap when DIMBREAK is on) */}
            {gap ? (
                <>
                    <Line points={[gap[0][0], gap[0][1]]} color={color} lineWidth={style.lineWeight} />
                    <Line points={[gap[1][0], gap[1][1]]} color={color} lineWidth={style.lineWeight} />
                </>
            ) : (
                <Line points={dimLine} color={color} lineWidth={style.lineWeight} />
            )}

            {/* Extension lines */}
            <Line points={ext1} color={color} lineWidth={style.lineWeight} />
            <Line points={ext2} color={color} lineWidth={style.lineWeight} />

            {/* Arrows */}
            <ArrowHead position={p1} angle={angle} size={style.arrowSize} color={color} />
            <ArrowHead position={p2} angle={angle + Math.PI} size={style.arrowSize} color={color} />

            {/* Text */}
            <Text
                position={textPos}
                color={dimStyleColor(style.textColor, ink)}
                fontSize={style.textHeight}
                anchorX="center"
                anchorY="bottom"
            >
                {displayText}
            </Text>

            {/* DIMINSPECT quality-control label above the text */}
            {data.inspection && (
                <Text
                    position={[textPos.x, textPos.y, textPos.z + style.textHeight * 1.4]}
                    color={color}
                    fontSize={style.textHeight * 0.8}
                    anchorX="center"
                    anchorY="bottom"
                >
                    {data.inspection}
                </Text>
            )}
        </group>
    );
};

/**
 * Radial Dimension Renderer
 */
export const RadialDimensionRenderer: React.FC<{
    data: RadialDimensionData;
    isSelected: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, isSelected, onClick }) => {
    const style = useDimStyleStore((s) => s.getStyleFor(data.styleId));
    const ink = useAnnotationInk();
    const color = isSelected ? '#00aaff' : dimStyleColor(style.lineColor, ink);

    const { line, textPos, angle, displayText } = useMemo(() => {
        const center = new THREE.Vector3(...data.center);
        const radPt = new THREE.Vector3(...data.radiusPoint);
        const angle = Math.atan2(radPt.y - center.y, radPt.x - center.x);

        let line: THREE.Vector3[];
        let textPos: THREE.Vector3;

        if (data.type === 'diameter') {
            // Diameter line goes through center
            const opposite = new THREE.Vector3(
                center.x - (radPt.x - center.x),
                center.y - (radPt.y - center.y),
                center.z
            );
            line = [opposite, radPt];
            textPos = center.clone();
        } else {
            // Radius line from center to edge
            line = [center, radPt];
            textPos = new THREE.Vector3().addVectors(center, radPt).multiplyScalar(0.5);
        }

        textPos.z += style.textGap; // lift above the ground plane (Z-up)

        const value = data.type === 'diameter' ? data.radius * 2 : data.radius;
        const prefix = data.type === 'diameter' ? 'Ø' : 'R';
        const displayText = data.override ||
            `${prefix}${(value * style.scale).toFixed(style.precision)}${style.suffix}`;

        return { line, textPos, angle, displayText };
    }, [data, style]);

    return (
        <group onClick={(e) => { e.stopPropagation(); onClick(e); }}>
            <Line points={line} color={color} lineWidth={style.lineWeight} />

            <ArrowHead
                position={new THREE.Vector3(...data.radiusPoint)}
                angle={angle + Math.PI}
                size={style.arrowSize}
                color={color}
            />

            <Text
                position={textPos}
                color={dimStyleColor(style.textColor, ink)}
                fontSize={style.textHeight}
                anchorX="center"
                anchorY="bottom"
            >
                {displayText}
            </Text>
        </group>
    );
};

/**
 * Arrow Head Component
 */
const ArrowHead: React.FC<{
    position: THREE.Vector3;
    angle: number;
    size: number;
    color: string;
}> = ({ position, angle, size, color }) => {
    const points = useMemo(() => {
        const tip = position.clone();
        const back1 = new THREE.Vector3(
            position.x - Math.cos(angle - 0.3) * size,
            position.y - Math.sin(angle - 0.3) * size,
            position.z
        );
        const back2 = new THREE.Vector3(
            position.x - Math.cos(angle + 0.3) * size,
            position.y - Math.sin(angle + 0.3) * size,
            position.z
        );
        return [back1, tip, back2];
    }, [position, angle, size]);

    return <Line points={points} color={color} lineWidth={2} />;
};
